import { DarkCard } from '@denqfabrik/landing-ui';
import type { ComponentType } from 'react';

/**
 * One numbered safety card: number, title, description, bullet list, illustration panel.
 *
 * Rendered 7x — 4x by ProcessSection (home page) and 3x by SicherheitPage, each of
 * which lays the same card out twice (stacked on mobile, zigzag on xl). Before this
 * component the body was copy-pasted at every one of those call sites.
 *
 * NOT the same thing as `SafetySection` in this folder — that one is the light-theme
 * principle grid with no bullets and no graphic.
 */
export interface SafetyPrinciple {
  number: string;
  title: string;
  description: string;
  bullets: readonly string[];
}

interface SafetyPrincipleCardProps {
  card: SafetyPrinciple;
  graphic: ComponentType;
  /** Tailwind height utility for the illustration panel: `h-[200px]` stacked, `h-[340px]` in the xl zigzag. */
  graphicHeightClass: string;
  /**
   * Appended to the card's own animation classes. Pass LITERAL utilities — Tailwind
   * scans source text, so a class assembled at runtime is never emitted into the CSS.
   */
  className?: string;
}

export default function SafetyPrincipleCard({
  card,
  graphic: Graphic,
  graphicHeightClass,
  className = '',
}: SafetyPrincipleCardProps) {
  return (
    <DarkCard
      className={`animate-fade-in-up [animation-duration:520ms] [animation-fill-mode:both] ${className}`}
    >
      <span className="text-sm font-semibold text-teal-400">{card.number}</span>
      <h3 className="mt-2 text-[1.4rem] font-medium leading-tight tracking-tight text-[#d5e4ff]">
        {card.title}
      </h3>
      <p className="mt-3 text-[0.94rem] leading-relaxed text-[var(--df-text-mid)]">
        {card.description}
      </p>
      <ul className="mt-5 space-y-2.5">
        {card.bullets.map((bullet) => (
          <li key={bullet} className="flex items-start gap-2.5 text-[0.88rem] text-[#c0d4ee]">
            <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 rounded-2xl bg-[rgba(20,38,72,0.65)] p-5">
        <div className={graphicHeightClass}>
          <Graphic />
        </div>
      </div>
    </DarkCard>
  );
}
